import { mapGetters } from 'vuex';
import _ from 'lodash';
import { getSelectedPlanListPayLoad } from '@/store/helper';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';
import BillingMessageBox from '@/components/common/message-box';
import { translation } from '@/plugins/i18n';
import { getSoftBundleMessages } from '@/views/config/soft-bundle/helper';
import { isEmptyVal } from '@/utils';
import { planTypes } from '@/common/constants';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';
import { softBundleUiUtils } from '@/views/config/soft-bundle/helper';

export default {
  data() {
    return {
      bd_planType: ''
    }
  },
  computed: {
    ...mapGetters(['softBundleConfiguration', 'getSelectedPlans', 'softBundleState']),
    _planType() {
      return this.planType || this.bd_planType;
    },
    /**
     *
     * @returns {SoftBundlePromotionBasicInfoBean}
     */
    softBundlePromotionBasicInfo() {
      return this.softBundleState?.basicInfo ?? {};
    },
    /**
     * @description get current plan with soft bundle configuration
     * @returns {SoftBundlePlanConfigBean}
     */
    softBundlePlanConfig() {
      return this.softBundleConfiguration?.[this._planType];
    },
    // It is obtained from bundlePlanPriceList
    soft_bundle_allPlanMap() {
      return this.softBundleState?.bundlePlanMap;
    },
    soft_bundle_allPlanList() {
      return _.values(this.soft_bundle_allPlanMap);
    },
    soft_bundle_currentPlanList() {
      return _.filter(this.soft_bundle_allPlanList, (plan) => plan?.type === this._planType);
    },
    soft_bundle_isContainCurrentPlan() {
      return this.soft_bundle_currentPlanList?.length;
    },
    // current plan's cycle depend on other plan switch billing cycle
    depOnBasePlanListSwitchCycle() {
      return _.chain(this.softBundleConfiguration ?? {})
        .keys()
        .filter((type) => {
          if (type !== this._planType) {
            const switchPlanTypeList = _.get(
              this.softBundleConfiguration,
              [type, 'billingCycleSwitch', 'switchPlanTypeList'],
              [],
            );

            return _.includes(switchPlanTypeList, this._planType);
          }

          return false;
        })
        .value();
    },
    // hidden plan billing cycle selection
    soft_bundle_hiddenCycleSelection() {
      return this.softBundlePlanConfig?.hiddenCycleSelection;
    },
    soft_bundle_disableBillingCycleSwitch() {
      return this.softBundlePlanConfig?.billingCycleSwitch?.disable;
    },
    soft_bundle_hiddenBasePlan() {
      return this.softBundlePlanConfig?.hiddenZpBasePlan;
    },
    soft_bundle_hiddenRemoveBtn() {
      return this.softBundlePlanConfig?.hiddenRemove;
    },
    soft_bundle_showRemoveBtn() {
      if (this.softBundlePlanConfig?.hiddenRemove) {
        return false;
      }
      return this.softBundlePlanConfig?.showRemove;
    },
    soft_bundle_showInAddon() {
      return this.softBundlePlanConfig?.showInAddon;
    },
    soft_bundle_showExploreOtherCategory() {
      return this.softBundlePlanConfig?.showSwitchCategoryEntry;
    },
    soft_bundle_hiddenExploreOtherPlans() {
      return this.softBundlePlanConfig?.hiddenSwitchCategoryEntry;
    },
    // current plan's from bundle plan config
    soft_bundle_enableBillingCycle() {
      return this.soft_bundle_disableBillingCycleSwitch
      ? _.get(this.soft_bundle_currentPlanList, [0, 'billing_cycle'], '')
      : '';
    },
    soft_bundle_config_planTypeList() {
      return _.keys(this.softBundleConfiguration) || [];
    },
    soft_bundle_showCustomCollapseContent() {
      const { planAutoCollapseWhenPreload } = _.get(this.softBundleConfiguration, ['config'], {});
      if (planAutoCollapseWhenPreload && !!this.softBundlePlanConfig) {
        return true;
      }
      return false;
    },
  },
  methods: {
    soft_bundle_getPlanQuantityTeleportInfo(needTeleport) {
      return softBundleUiUtils.getPlanQuantityTeleportInfo(needTeleport, this._planType);
    },
    async validateSoftBundlePlanWhenRemove(callback) {
      try {
        // config is need pre validate
        if (!this.softBundlePlanConfig?.shouldValidateWhenRemove) {
          callback?.(couponFetchStatus.idle);
          return Promise.resolve();
        }

        // if do not have soft bundle info or last validate status is fail
        const softBundleBasicInfo = this?.softBundlePromotionBasicInfo;
        if (isEmptyVal(softBundleBasicInfo) || softBundleBasicInfo?.status === couponFetchStatus.failure) {
          callback?.(couponFetchStatus.failure);
          return Promise.resolve();
        }

        const selectedPlanList = _.filter(this.getSelectedPlans, (plan) => plan.type !== this._planType);
        const validateResult = await softBundleBasicInfo?.validate({
          pureValidate: true,
          currentSelectedPlans: selectedPlanList,
          formatParams: (params) => {
            if (params?.orderData?.planList) {
              params.orderData.planList = getSelectedPlanListPayLoad(selectedPlanList);
            }
            return params;
          }
        });

        // Execute callback after validation is complete
        callback?.(validateResult);

        // Execute popup message handling and return the result
        const popupResult = await this.popUpMessageWithSoftBundlePlanWhenRemove(validateResult);
        return popupResult;
      } catch (error) {
        // Handle any errors during validation
        console.error('Error during soft bundle validation:', error);
        callback?.(couponFetchStatus.failure);
        return Promise.reject(error);
      }
    },
    async popUpMessageWithSoftBundlePlanWhenRemove(validateResult) {
      if (validateResult === couponFetchStatus.failure) {
        const bundleName = this.softBundleState?.bundleName;
        const name = translation(getSoftBundleMessages(bundleName)?.name) ?? bundleName;
        const title = translation('softBundle.removePlanBlockInfo.title');
        const message= translation('softBundle.removePlanBlockInfo.description', [name]);

        const planKey = planTypes[this._planType];
        const trackingPlanName = getTrackingProductNameByPlanType(planKey) || 'zoom device';
        const trackingModalTitle = `${trackingPlanName} options`;
        const trackingModalDesc = `${trackingPlanName} - remove bundle item modal - impression`;
        this.$tracking.buyInteraction(
          trackingModalTitle,
          trackingModalDesc
        );

        return BillingMessageBox.confirm(message, title, {
          confirmButtonText: translation('common.btn_continue'),
          cancelButtonText: translation('common.btn_cancel'),
          closeOnClickModal: false,
          roundButton: true,
          customClass: 'partner-refer-message-popup',
          dangerouslyUseHTMLString: true,
        }).catch(() => {
          this.$tracking.buyInteraction(
            trackingModalTitle,
            `${trackingPlanName} - remove bundle item modal - cancel`
          );
          return Promise.reject('cancel');
        }).then(() => {
          this.$tracking.buyInteraction(
            trackingModalTitle,
            `${trackingPlanName} - remove bundle item modal - continue`
          );
          return Promise.resolve('continue');
        });
      }

      return Promise.resolve();
    },
  },
};
